/**
 * Product analytics + frontend error reporting + session replay (PostHog).
 *
 * Single integration point for the portal. Everything funnels through the
 * helpers below so call sites never touch `posthog` directly — that keeps the
 * "is it configured?" guard, the privacy posture, and the event vocabulary in
 * one place.
 *
 * Configuration is env-driven (`VITE_POSTHOG_KEY`, `VITE_POSTHOG_HOST`). When
 * the key is absent — local dev, CI, prerender — every export is a safe no-op,
 * so nothing has to special-case "analytics off". This means a missing key
 * never throws and never blocks a render.
 *
 * Privacy: PostHog runs only in the portal shell, never inside a game iframe
 * (the iframe is a separate document), which preserves the "no third-party
 * trackers in any game iframe" promise on /privacy. Person profiles are
 * `identified_only` so anonymous K-12 visitors don't accrue profiles until an
 * account actually signs in. Session replay masks all inputs (and any
 * `data-ph-mask` element) so we capture behavior, never typed PII.
 *
 * CLASSROOM STUDENTS (COPPA) — the hard rule: kids are tracked ANONYMOUS-ONLY.
 * A classroom student (me.classroom_student set) is never identify()'d, never
 * gets a person profile, and session replay is off on every student surface
 * (see isStudentSurface / AnalyticsReplayGuard). Their funnel events carry an
 * anonymous distinct_id only, and that id is severed in BOTH directions on
 * shared devices: an identified adult is reset() before kid events fire, and
 * an anon id that carried kid events is reset() before any later adult
 * identify() (sa:kid-anon marker) so a kid's history can never merge into a
 * parent's person. Never put kid names, PINs, phone numbers, or free text in
 * event props. Known accepted capture: /c/:code pageview URLs contain the
 * class code (semi-public — it's on classroom whiteboards; PostHog access is
 * internal staff). If policy tightens, rewrite it via a `before_send` hook.
 */

import type { PostHog } from "posthog-js";
import type { MeResponse } from "./types.js";
import type { FeedbackCallSource } from "./feedback-call.js";
import {
  ALL_ATTRIBUTION_KEYS,
  attributionProps,
  getAttribution,
  personAttribution,
  type Attribution,
} from "./attribution.js";
import { TL_FLAG_KEY, isTlVariant, readAssignedVariant, type TlVariant } from "./tl-experiment.js";

// PostHog is loaded LAZILY (dynamic import) and its init is deferred to idle /
// first-interaction by the caller (main.tsx) — this keeps its ~130 KB bundle and
// session recorder out of the eager entry chunk and off the critical-path /
// TBT window (mobile Lighthouse). Because init is async, calls made before the
// chunk resolves (ad_visit, attribution super-props, experiment exposure —
// all fired at boot) would be dropped; instead they're QUEUED here and replayed
// in order once PostHog is live, so the deferral never loses events.
let posthog: PostHog | null = null;
let booting = false;
const pending: Array<(ph: PostHog) => void> = [];

/**
 * Marks that the CURRENT anonymous id has carried classroom-student events on
 * this device. localStorage (not session) because PostHog's own anon id also
 * lives in localStorage — the poisoned id survives the tab, so the marker must
 * too. Checked (and cleared) before any adult identify().
 */
const KID_ANON_KEY = "sa:kid-anon";

function readKidAnonMarker(): boolean {
  try {
    return localStorage.getItem(KID_ANON_KEY) === "1";
  } catch {
    return false;
  }
}

function clearKidAnonMarker(): void {
  try {
    localStorage.removeItem(KID_ANON_KEY);
  } catch {
    /* storage blocked — nothing to clear */
  }
}

/** Flag the current anonymous id as a kid's. Call whenever a classroom student
 *  is (or is about to be) the active user on this device. */
export function markKidAnonSession(): void {
  try {
    localStorage.setItem(KID_ANON_KEY, "1");
  } catch {
    /* storage blocked — the identify-side reset still protects via _isIdentified */
  }
}

/** posthog.reset() wipes ALL super props (it clears persistence), including the
 *  `app: games_portal` stamp every dashboard filters on. Every reset in this
 *  module must go through here so post-reset events stay attributable. */
function resetKeepingSuperProps(ph: PostHog): void {
  ph.reset();
  ph.register({ app: "games_portal" });
}

function isIdentified(ph: PostHog): boolean {
  // _isIdentified is underscore-prefixed but present in posthog-js typings;
  // $user_state is the persisted fallback for older builds.
  if (typeof ph._isIdentified === "function") return ph._isIdentified();
  return (ph.get_property("$user_state") as string | undefined) === "identified";
}

/**
 * Cut any identified adult loose so subsequent events (a classroom student's
 * funnel) run on a fresh anonymous id. No-op when already anonymous — a kid
 * reloading mid-homework keeps ONE anon id, so their funnel stays stitched.
 * Deliberately not gated on any per-page-load ref: PostHog identity persists
 * in localStorage, so a teacher identified in yesterday's session must still
 * be severed today.
 */
export function ensureAnonymousSession(): void {
  withPosthog((ph) => {
    if (!isIdentified(ph)) return;
    resetKeepingSuperProps(ph);
  });
}

/**
 * Run `fn` against the live PostHog, or queue it until the lazy chunk resolves.
 * A true no-op only when analytics is off (no key / SSR) — there `booting` stays
 * false so nothing accumulates.
 */
function withPosthog(fn: (ph: PostHog) => void): void {
  if (posthog) fn(posthog);
  else if (booting) pending.push(fn);
}

/**
 * Student (COPPA) surfaces: session replay is always off here, and visitors on
 * the join surfaces are presumptively kids. `/homework` is the shared role
 * gate — replay off (kids land there mid-funnel; adults bounce within a tick).
 * NOTE: bare `/c` must NOT match — that's the landing-gallery variant "c";
 * only `/c/:code` is the classroom join deep link.
 */
export function isStudentSurface(pathname: string): boolean {
  return (
    pathname === "/join" ||
    pathname === "/homework" ||
    pathname === "/student" ||
    pathname.startsWith("/student/") ||
    /^\/c\/./.test(pathname)
  );
}

// null = the replay guard hasn't reported yet; init falls back to the URL.
let replayAllowed: boolean | null = null;

/**
 * Turn session recording on/off (AnalyticsReplayGuard drives this from the
 * current route + classroom-student state). Queue-safe: a call landing before
 * the lazy chunk resolves replays at init, and the init config below seeds the
 * recorder's INITIAL state from the same signal so no pre-queue frames leak.
 */
export function setReplayAllowed(allowed: boolean): void {
  if (replayAllowed === allowed) return;
  replayAllowed = allowed;
  withPosthog((ph) => (allowed ? ph.startSessionRecording() : ph.stopSessionRecording()));
}

/**
 * Boot PostHog once, at client startup. No-ops during SSR/prerender (no
 * `window`) and when no project key is configured. The heavy work is deferred:
 * posthog-js is dynamically imported (so it's code-split out of the eager
 * bundle) and the caller schedules this on idle/first-interaction.
 */
export function initAnalytics(): void {
  if (typeof window === "undefined") return;
  if (booting || posthog) return;
  const key = import.meta.env.VITE_POSTHOG_KEY;
  if (!key) return;
  booting = true;
  const host = import.meta.env.VITE_POSTHOG_HOST ?? "https://us.i.posthog.com";

  // Test-landing experiment (TEX-1637): the index.html pre-paint script
  // assigned a variant before any module ran. Bootstrap the flag with it so
  // getFeatureFlag() is correct synchronously (the /flags response hasn't
  // arrived at first render), and skip the automatic first-load /flags request
  // — it wouldn't carry `tl_variant` yet, so PostHog's hash-based fallback
  // could briefly disagree with what the visitor is already seeing. The
  // setPersonPropertiesForFlags call below fires the (single) /flags request
  // WITH the property, and the flag's per-condition variant overrides pin the
  // server's answer to the same local assignment.
  //
  // Scope note: the pre-paint script runs on EVERY portal page (assignment ≠
  // exposure), so in a real browser `tlVariant` is always set and this whole
  // block — including `advanced_disable_feature_flags_on_first_load` — is
  // effectively portal-global, not test-landing-only. Two consequences:
  //  1. The init option and the setPersonPropertiesForFlags reload are a
  //     PAIRED unit. The option suppresses the automatic first /flags; the
  //     reload is what fetches flags at all. Remove one ⇒ remove both, or
  //     flags never load.
  //  2. Any FUTURE portal flag gets its server value when that reload
  //     resolves — the same post-init timing as the default first load — but
  //     it is NOT bootstrapped: synchronous reads before the response return
  //     undefined. Bootstrap new flags here or read them via onFeatureFlags.
  const tlVariant = readAssignedVariant();

  void import("posthog-js")
    .then(({ default: ph }) => {
      ph.init(key, {
        api_host: host,
        // `2025-05-24` switches pageview capture to `history_change`, which tracks
        // client-side route changes in this SPA without a manual router listener.
        defaults: "2025-05-24",
        // Frontend error tracking: window.onerror + unhandledrejection. React
        // error boundaries swallow render errors, so ErrorBoundary reports those
        // explicitly via captureError() below.
        capture_exceptions: true,
        // Don't materialize a person profile for anonymous visitors — only once a
        // real user is identified. Cheaper and privacy-friendlier for K-12 traffic.
        person_profiles: "identified_only",
        // Session replay — so we can watch where signups stall (e.g. the large
        // cohort that completes the pre-diagnostic signup form but never starts the
        // diagnostic). Capturing must ALSO be turned on in the PostHog project
        // settings; this client config only sets the privacy posture. Inputs are
        // fully masked because this is a K-12 surface that collects PII (email,
        // phone, child names) at signup — we record behavior (clicks, navigation,
        // scroll), never the characters a minor types. Because init itself is
        // idle-deferred, the recorder now also loads after the page is interactive.
        // Initial state comes from the replay guard when it has already reported,
        // else from the boot URL — closes the window where the recorder could
        // start on a student surface before a queued stop call replays.
        disable_session_recording:
          replayAllowed !== null ? !replayAllowed : isStudentSurface(window.location.pathname),
        session_recording: {
          maskAllInputs: true,
          // Mask any element opted in via `data-ph-mask` (e.g. rendered PII like a
          // displayed email/phone) on top of the input masking above.
          maskTextSelector: "[data-ph-mask]",
        },
        // Surveys are unused on the portal; disabling stops posthog-js from
        // fetching surveys.js (dead weight in the Lighthouse JS payload).
        disable_surveys: true,
        ...(tlVariant
          ? {
              bootstrap: { featureFlags: { [TL_FLAG_KEY]: tlVariant } },
              advanced_disable_feature_flags_on_first_load: true,
            }
          : {}),
      });

      // COPPA — sever a persisted adult identity SYNCHRONOUSLY at init when
      // this boot is on a student surface (or the device carries the kid-anon
      // marker). The queued ensureAnonymousSession from AnalyticsIdentity
      // cannot cover this: the pending queue drains AFTER init, but the
      // automatic initial $pageview materializes at init — on a shared device
      // it would land on yesterday's teacher/parent profile. This block (and
      // the register below) provably runs before that pageview event is
      // built: the initial pageview carries the `app` super prop registered
      // in this same synchronous block.
      if ((isStudentSurface(window.location.pathname) || readKidAnonMarker()) && isIdentified(ph)) {
        ph.reset();
      }

      // Stamp every event (incl. autocapture, $pageview, $exception) with the
      // product surface — all dashboards/insights filter on `app = games_portal`.
      // Historical note: the portal once shared project 231991 with the marketing
      // site; it now has its own project (463200, staging 463553) but the stamp
      // stays load-bearing for the dashboard filters and any future re-merge.
      // Registered AFTER the sever above (reset() wipes super props); any other
      // reset() must route through resetKeepingSuperProps.
      ph.register({ app: "games_portal" });

      if (tlVariant) ph.setPersonPropertiesForFlags({ tl_variant: tlVariant });

      posthog = ph;
      // Replay anything requested while the chunk was loading, in call order.
      const queued = pending.splice(0);
      for (const fn of queued) fn(ph);
    })
    .catch(() => {
      // The posthog-js chunk failed to load (offline, CDN blip, a content blocker
      // that blocks the chunk URL). Without this, `booting` stays true forever:
      // every later withPosthog() call would push into `pending` that can never
      // drain (an unbounded leak), and the `booting || posthog` guard would block
      // any retry. Reset so analytics simply stays OFF this session (calls no-op)
      // and a future initAnalytics() could retry.
      booting = false;
      pending.length = 0;
    });
}

/**
 * The test-landing experiment arm per PostHog, registering exposure as a side
 * effect (bridge framing: fun vs competition).
 *
 * Goes through PostHog when live — `getFeatureFlag` both returns the
 * bootstrapped/pinned value AND emits the `$feature_flag_called` exposure
 * event the experiment counts people by (posthog-js dedupes repeat calls per
 * flag+value, so calling again is safe). Falls back to the pre-paint local
 * assignment when analytics is off (dev, no key, blocked); last resort is
 * control.
 *
 * Because it captures an event, this belongs in a mount EFFECT, not in render
 * or a useState initializer — the template renders from
 * `readAssignedVariant()` directly (the flag is pinned to the same value) and
 * calls this from useEffect purely to register the exposure.
 *
 * Deliberate: a disabled/deleted flag in PostHog also lands in the fallback —
 * visitors keep their assigned arm rather than all snapping to control
 * mid-experiment (which would flash assigned-arm → control at React mount on
 * every prerendered page). Ending the experiment is a code change + deploy;
 * see _docs/experiments/2026-06-test-landing-bridge.md.
 */
export function getTestLandingVariant(): TlVariant {
  if (typeof window === "undefined") return "control"; // SSR stacks both arms itself
  if (posthog) {
    const v = posthog.getFeatureFlag(TL_FLAG_KEY);
    if (isTlVariant(v)) return v;
  } else if (booting) {
    // PostHog still lazy-loading — queue the exposure so it registers the moment
    // the chunk resolves (getFeatureFlag emits `$feature_flag_called`; posthog-js
    // dedupes it). The return below uses the pinned local assignment meanwhile.
    pending.push((ph) => void ph.getFeatureFlag(TL_FLAG_KEY));
  }
  return readAssignedVariant() ?? "control";
}

/**
 * Register the acquisition stash as durable super properties. Once set,
 * PostHog stamps EVERY subsequent event (autocapture, `$pageview`, `$exception`,
 * and our funnel events) with the `ad_*` props — including events from anonymous
 * visitors who never sign in — so we can answer "how many leads/visits came from
 * Facebook / cold email (and which campaign/ad)" without a person profile.
 * Every visitor with a stash gets `ad_landing_path` / `ad_referrer` /
 * `ad_first_seen_at`; tagged ones also get the campaign keys. Idempotent and
 * safe to call on every boot; a no-op when analytics is off or the stash is
 * absent (every storage layer blocked).
 *
 * Last-touch semantics: a later tagged arrival REPLACES the campaign block, so
 * any `ad_*` key it no longer carries (a June `ad_fbclid` after a September
 * cold-email click) must come OFF the super props too — `register()` alone
 * would leave it stamped on every event forever.
 */
export function registerAttribution(attribution: Attribution | null): void {
  const props = attributionProps(attribution);
  if (Object.keys(props).length === 0) return;
  withPosthog((ph) => {
    for (const key of ALL_ATTRIBUTION_KEYS) {
      const prop = `ad_${key}`;
      if (!(prop in props)) ph.unregister(prop);
    }
    ph.register(props);
  });
}

/**
 * Tie subsequent events to a signed-in user and attach segmentation traits.
 * Keep traits to what we actually slice on (role, grade, premium) plus the
 * email needed to recognize the account in the PostHog UI. The acquisition
 * stash rides along as `$set_once` person props so a converted user keeps the
 * campaign / referrer / landing that referred them — frozen at first identify,
 * never overwritten — and ONLY when that touch predates the account
 * (`personAttribution`): an existing teacher clicking a tagged cold email must
 * not be re-filed as cold_email-sourced.
 */
export function identifyUser(me: MeResponse): void {
  const user = me.user;
  if (!user) return;
  // COPPA choke point: classroom students are never identified. Belt-and-
  // braces with AnalyticsIdentity's own guard — every identify path funnels
  // through here.
  if (me.classroom_student) return;
  withPosthog((ph) => {
    // If this device's current anonymous id carried a kid's events, identify()
    // would retroactively merge that whole history into this adult's person
    // (personless events join on distinct_id at query time). Sever first.
    if (readKidAnonMarker()) {
      resetKeepingSuperProps(ph);
      clearKidAnonMarker();
    }
    ph.identify(
      user.id,
      {
        email: user.email ?? undefined,
        role: user.role ?? undefined,
        grade: user.grade ?? undefined,
        premium: me.premium,
        marketing_opt_in: user.marketing_opt_in,
      },
      personAttribution(getAttribution(), user.created_at),
    );
  });
}

/** Clear the identified user on sign-out so the next session starts anonymous. */
export function resetUser(): void {
  clearKidAnonMarker(); // the reset below mints a fresh anon id — marker is moot
  withPosthog((ph) => resetKeepingSuperProps(ph));
}

/**
 * Report a caught error (e.g. from the React error boundary, which PostHog's
 * autocapture can't see). `posthog.captureException` handles stack-trace
 * extraction and source-map resolution.
 */
export function captureError(error: unknown, properties?: Record<string, unknown>): void {
  withPosthog((ph) => ph.captureException(error, properties));
}

/** Low-level event capture. Prefer the named helpers in `track` below. */
function capture(event: string, properties?: Record<string, unknown>): void {
  withPosthog((ph) => ph.capture(event, properties));
}

/** Whether the visitor's state has filed with the IRS (derived from core, never the client). */
export type FundingStateStatus = "filed" | "not_filed";

/**
 * Which capture surface an event came from: the quiz's trade screen, its
 * plain capture screen (after "changed my mind"), or the /funding-brief
 * article's single capture card.
 */
export type FundingScreen = "trade" | "capture" | "brief_capture";

/**
 * The segmentation every /funding-check result-and-after event carries. Keys
 * are the quiz's stable answer keys (@stratacademy/core), `state` the 2-letter
 * code; an answer the visit never gave (a re-send URL skips Q1/Q4) is
 * "unknown". NEVER a phone number or an email address.
 */
export interface FundingResultProps {
  state: string;
  state_status: FundingStateStatus;
  awareness: string;
  grade_band: string;
  school_type: string;
  how_find: string;
}

/**
 * Funnel + behavior events. Named wrappers keep event strings and their
 * property shapes consistent across call sites (landing → signup → placement →
 * play → subscribe).
 */
export const track = {
  /**
   * First-touch ad visit — fired once per attributed session, the moment we
   * capture marketing params off the landing URL (BEFORE any signup). This is
   * the countable top-of-funnel "an ad sent us a visitor" signal: slice by
   * `source` / `campaign_id` / `ad_id` / `fbclid` / `utm_*` to see Facebook (and
   * per-campaign/ad) lead volume, even for anonymous visitors who never convert.
   */
  adVisit: (attribution: Attribution) => capture("ad_visit", { ...attribution }),

  signupCompleted: (props: { role: "parent" | "student" }) => capture("signup_completed", props),

  /**
   * A Quiz Studio quiz was created — fired at the single create funnel point,
   * tagged with the input MODALITY so we can see how often each source is used:
   * notes (paste) vs pdf vs word vs image (TEX-2255). `char_count` is the length
   * of the study text we sent on.
   */
  quizCreateSubmitted: (props: {
    modality: "paste" | "pdf" | "docx" | "image";
    char_count: number;
  }) => capture("quiz_create_submitted", props),

  pseoQuizViewed: (props: { quiz_id: string; subject: string; source_key?: string }) =>
    capture("pseo_quiz_viewed", props),
  pseoAnswerRevealed: (props: { quiz_id: string; question_id: string; subject: string }) =>
    capture("pseo_answer_revealed", props),
  pseoQuizStarted: (props: { quiz_id: string; subject: string; game_slug: string }) =>
    capture("pseo_quiz_started", props),

  /**
   * PostHog-side Lead — fired exactly where the Meta pixel Lead fires (phone
   * verified via OTP at signup; a confirmed contactable number is the real
   * lead). Primary metric of the test-landing experiment (TEX-1637):
   * `variant`/`test_slug` ride along only when the visitor was actually
   * exposed to a test landing page (see tl-experiment.ts stampExposure).
   */
  leadCaptured: (props: {
    source: "popup" | "signup_page" | "teach_signup";
    variant?: string;
    test_slug?: string;
  }) => capture("lead_captured", props),

  /**
   * The post-Lead onboarding question was answered ("What worries you most
   * about the {test}?", TEX-1639) — the step between `lead_captured` and
   * `placement_started` for test-landing signups, so this is the funnel-health
   * signal for the drop-off that step introduces. The answer text itself goes
   * only to the API (lead_test_worries); never put it in PostHog.
   */
  testWorrySubmitted: (props: { test_slug: string; variant?: string }) =>
    capture("test_worry_submitted", props),

  placementStarted: (props: { diagnostic_run_id: string }) => capture("placement_started", props),

  /**
   * A diagnostic phase's question batch finished (the intermission screen is
   * about to show). The depth signal between `placement_started` and
   * `placement_completed`: filtering drop-offs by the highest `phase_number`
   * reached shows WHERE inside the diagnostic students bail.
   */
  placementPhaseCompleted: (props: { diagnostic_run_id: string; phase_number: number }) =>
    capture("placement_phase_completed", props),

  /**
   * The diagnostic reached a terminal state and produced a placement — the
   * "completed the diagnostic" funnel step (any termination reason: full run,
   * early stop, or gate stop all count; each writes a placements row).
   */
  placementCompleted: (props: { diagnostic_run_id: string; placement_id: string }) =>
    capture("placement_completed", props),

  /**
   * The student left the diagnostic without finishing (Exit button / leave
   * guard / play timeout). Explicit abandonment beat for the funnel's "why":
   * `phase_number` is the phase they were in when they quit.
   */
  placementExited: (props: {
    diagnostic_run_id: string;
    phase_number: number;
    reason: "user_exit" | "timeout";
  }) => capture("placement_exited", props),

  /**
   * Play CTA pressed on a game detail page — fires for ALL games regardless of
   * whether the click is allowed, gated by sign-in, or gated by paywall. This
   * is the cross-catalog popularity + intent signal (game_started only fires
   * for plays that actually start, i.e. after any gate is cleared).
   */
  playClicked: (props: {
    game_slug: string;
    outcome: "allowed" | "needs_signup" | "needs_upgrade";
  }) => capture("play_clicked", props),

  gameStarted: (props: {
    game_slug: string;
    play_id: string;
    queue_kind: "public" | "private" | "solo";
  }) => capture("game_started", props),

  gameEnded: (props: {
    game_slug: string;
    play_id: string;
    reason: string;
    questions_answered?: number;
  }) => capture("game_ended", props),

  subscribeCheckoutStarted: (props?: { has_coupon: boolean }) =>
    capture("subscribe_checkout_started", props),

  subscribeSucceeded: () => capture("subscribe_succeeded"),

  /**
   * An upsell surface was shown. `kind` separates the sign-in wall ("auth")
   * from the premium paywall ("paywall"); `source` is what triggered it
   * (dashboard, placement-test, game-play, premium). Pair with
   * `upsellCtaClicked` to get a per-surface click-through rate.
   */
  upsellViewed: (props: { kind: "auth" | "paywall"; source: string }) =>
    capture("upsell_viewed", props),

  /** A button inside an upsell surface was clicked. `cta` identifies which. */
  upsellCtaClicked: (props: { kind: "auth" | "paywall"; source: string; cta: string }) =>
    capture("upsell_cta_clicked", props),

  /**
   * The feedback-call ($50 gift card) offer was surfaced on a placement-funnel
   * page (TEX-1997). `source` is the funnel surface, so we can compare which
   * touchpoint drives bookings. Pair with `feedbackCallCtaClicked` for a
   * per-surface click-through rate.
   */
  feedbackCallOfferViewed: (props: { source: FeedbackCallSource }) =>
    capture("feedback_call_offer_viewed", props),

  /** The CTA inside a feedback-call offer was clicked. */
  feedbackCallCtaClicked: (props: { source: FeedbackCallSource }) =>
    capture("feedback_call_cta_clicked", props),

  /**
   * The /feedback-call booking page mounted. `source` is the surface the
   * visitor arrived from (querystring). Booking COMPLETION is not tracked —
   * that lives in the external scheduler (no Calendly/Cal.com webhook wired up).
   */
  feedbackCallPageViewed: (props: { source: string }) =>
    capture("feedback_call_page_viewed", props),

  // ─── /funding-check (federal scholarship quiz — ad-only landing) ──────────
  // Account-less lead funnel: started → answered ×5 → result → trade →
  // consent → phone captured. ONE parameterized `funding_quiz_answered`
  // (questions get reordered; five named events would break every funnel).
  // Deliberately NOT `lead_captured`: that means an OTP-verified number and
  // this flow has no OTP. The metric that matters is consented contacts.
  /**
   * The intro screen mounted. Carries the raw utm_* + fbclid from the stash;
   * viewed − started is the entry bounce, the primary ad-to-page read.
   */
  fundingQuizViewed: (props: Record<string, string>) => capture("funding_quiz_viewed", props),
  /** The intro CTA was clicked (the mount is `funding_quiz_viewed`). Same props. */
  fundingQuizStarted: (props: Record<string, string>) => capture("funding_quiz_started", props),
  /**
   * `answer` is the literal option text — the ad team builds remarketing
   * pools on it in Meta and PostHog; `answer_key` is the stable key the lead
   * row stores.
   */
  fundingQuizAnswered: (props: {
    question_number: number;
    question_key: string;
    answer: string;
    answer_key: string;
  }) => capture("funding_quiz_answered", props),
  /**
   * The full brief rendered — the answer, shown before anything is asked.
   * `seeded` = opened from a re-send URL (a text, a retargeting ad), no
   * questions asked.
   */
  fundingResultShown: (props: FundingResultProps & { seeded: boolean }) =>
    capture("funding_result_shown", { ...props }),
  fundingTradeShown: (props: { state_status: FundingStateStatus; screen: FundingScreen }) =>
    capture("funding_trade_shown", props),
  /** The consent checkbox was ticked (not fired on untick). The metric that matters. */
  fundingConsentChecked: (props: FundingResultProps & { screen: FundingScreen }) =>
    capture("funding_consent_checked", { ...props }),
  /** The API accepted the lead. `sms` is the confirmation-text outcome it reported. */
  fundingPhoneCaptured: (props: FundingResultProps & { screen: FundingScreen; sms: string }) =>
    capture("funding_phone_captured", { ...props }),
  /**
   * The lead POST did not return 2xx. `reason` is the API's error code, else
   * `http_<status>`, else `unknown` (fetch itself rejected: network / CORS);
   * `status` is null when no response arrived. `invalid_phone` and `http_429`
   * are normal traffic; anything else means the page is down. The 2026-09-04
   * 500s left no client-side trace at all — this is that trace.
   */
  fundingCaptureFailed: (props: { reason: string; status: number | null; screen: FundingScreen }) =>
    capture("funding_capture_failed", props),
  fundingDeclined: (props: { screen: FundingScreen }) => capture("funding_declined", props),
  /** "Changed my mind, text me instead" after declining — the decline copy did its job. */
  fundingReconsidered: (props: FundingResultProps) => capture("funding_reconsidered", { ...props }),
  /** The optional email on the done screen was saved (its own consent). Never the address. */
  fundingEmailCaptured: (props: FundingResultProps) =>
    capture("funding_email_captured", { ...props }),
  /** The free placement test pill on the done screen — the cross-sell into the product. */
  fundingProductClicked: () => capture("funding_product_clicked"),

  // ─── /funding-brief (the article destination — the A/B against the quiz) ──
  /** The article mounted. `headline_variant` = which utm_content headline rendered, plus the raw utm_* and fbclid. */
  fundingBriefViewed: (props: Record<string, string> & { headline_variant: string }) =>
    capture("funding_brief_viewed", props),
  /** Scrolled past 60% of the article — read, not bounced. Once per visit. */
  fundingBriefRead: () => capture("funding_brief_read"),
  /**
   * Typed into the state ledger's search. `query` is the lower-cased text ONLY
   * when it is a prefix of a real state name (otherwise null — a pasted name,
   * email or phone number is counted, never sent); `matches` = how many states
   * it matched; `state` = the code when exactly one did.
   */
  fundingStateLookedUp: (props: { query: string | null; matches: number; state: string | null }) =>
    capture("funding_state_looked_up", props),

  // ─── Classrooms & homework funnel ─────────────────────────────────────────
  // Three funnels (teacher / student / parent) + shared surfaces. STUDENT
  // events fire from ANONYMOUS sessions only (see the COPPA rule in the header
  // — AnalyticsIdentity guarantees the distinct_id is never a kid's identity)
  // and must never carry kid names, PINs, phone numbers, class codes, or free
  // text. `assignment_completed` is captured SERVER-side (the authoritative
  // moment lives in the attempts hook); `homework_completed` below is the
  // behavioral twin (the kid actually saw the win screen).

  // Teacher: landing → signup steps → class → assignment → report.
  teacherLandingCtaClicked: (props: { placement: "nav" | "hero" | "closer" }) =>
    capture("teacher_landing_cta_clicked", props),
  teacherSignupOtpSent: (props: { mode: "signup" | "signin" }) =>
    capture("teacher_signup_otp_sent", props),
  teacherPhoneVerified: () => capture("teacher_phone_verified"),
  teacherProfileCompleted: () => capture("teacher_profile_completed"),
  /** Any teacher-signup step failed. One event, sliced by `step`/`reason`. */
  teacherSignupFailed: (props: { step: "phone" | "code" | "about" | "class"; reason: string }) =>
    capture("teacher_signup_failed", props),
  classroomCreated: (props: {
    classroom_id: string;
    grade: number | null;
    source: "signup" | "teach_home";
  }) => capture("classroom_created", props),
  /** Class creation was refused. `reason` is the API's machine code, so the
   *  class cap and the route's rate limiter (both 429) stay distinguishable. */
  classroomCreateFailed: (props: { reason: string; source: "signup" | "teach_home" }) =>
    capture("classroom_create_failed", props),
  /** `surface`: the class card on /teach, the full-screen present overlay on
   *  class home, or a homework report's empty card. Each is a distinct place a
   *  teacher reaches for the code, so they stay distinct members rather than
   *  one being stretched to cover another — that would make the existing rows
   *  ambiguous after the fact. */
  classCodeCopied: (props: { surface: "card" | "class_home" | "homework_report" }) =>
    capture("class_code_copied", props),
  classJoinLinkCopied: () => capture("class_join_link_copied"),
  /** "Present to class" projector takeover opened. */
  classCodePresented: () => capture("class_code_presented"),
  parentInviteResent: (props: {
    source:
      | "roster"
      | "attention_banner"
      | "student_lock"
      | "student_banner"
      | "student_expired"
      | "student_player";
  }) => capture("parent_invite_resent", props),
  parentInviteResendFailed: (props: {
    source:
      | "roster"
      | "attention_banner"
      | "student_lock"
      | "student_banner"
      | "student_expired"
      | "student_player";
    /** `declined` = the parent replied STOP; a resend is refused as a resolved state. */
    reason: "invalid_phone" | "throttled" | "declined" | "unknown";
  }) => capture("parent_invite_resend_failed", props),
  /** Teacher corrected a bad parent number and re-sent the invite. */
  parentPhoneFixed: (props?: { source: "row" | "menu" | "attention_banner" }) =>
    capture("parent_phone_fixed", props),
  /** Teacher tapped the class banner's "Fix numbers →" (count = bounced kids). */
  parentFixNumbersBannerClicked: (props: { count: number }) =>
    capture("parent_fix_numbers_banner_clicked", props),
  /** `rejected` counts the lines the SERVER refused (bad lines are stopped
   *  client-side and never attempted), so added + rejected < attempted means a
   *  full class cut the run short. */
  rosterStudentsAdded: (props: { added: number; attempted: number; rejected: number }) =>
    capture("roster_students_added", props),
  studentPinReset: () => capture("student_pin_reset"),
  studentRemoved: () => capture("student_removed"),
  classroomArchived: () => capture("classroom_archived"),
  /** Class name edited after creation (PATCH /v1/classrooms/:id with `name`). */
  classroomRenamed: () => capture("classroom_renamed"),
  /** Restored from the archive — the inverse of classroom_archived. `source`
   *  separates the /teach archived shelf from the archived class's own banner. */
  classroomUnarchived: (props: { source: "teach_home" | "class_home" }) =>
    capture("classroom_unarchived", props),

  // Assignment wizard (steps 1 content / 2 schedule / 3 review).
  assignmentWizardOpened: (props: { classroom_id: string }) =>
    capture("assignment_wizard_opened", props),
  assignmentWizardStepCompleted: (props: {
    from_step: number;
    to_step: number;
    question_count: number;
  }) => capture("assignment_wizard_step_completed", props),
  assignmentWizardOwnMaterialOpened: () => capture("assignment_wizard_own_material_opened"),
  /** AI question generation from teacher-supplied material (the hero
   *  differentiator) — started/ready/failed measure its real-world success rate. */
  assignmentQuizGenerationStarted: (props: { char_count: number }) =>
    capture("assignment_quiz_generation_started", props),
  assignmentQuizGenerationReady: (props: { question_count: number }) =>
    capture("assignment_quiz_generation_ready", props),
  assignmentQuizGenerationFailed: (props: { reason: string }) =>
    capture("assignment_quiz_generation_failed", props),
  assignmentQuizAttached: (props: { question_count: number }) =>
    capture("assignment_quiz_attached", props),
  assignmentQuizDiscarded: () => capture("assignment_quiz_discarded"),
  /** The teacher-funnel finish line: homework actually assigned. */
  assignmentCreated: (props: {
    assignment_id: string;
    classroom_id: string;
    question_count: number;
    topic_count: number;
    quiz_count: number;
    est_minutes: number;
    days_open: number;
  }) => capture("assignment_created", props),
  assignmentCreateFailed: (props: { reason: string }) => capture("assignment_create_failed", props),
  assignmentReportViewed: (props: {
    assignment_id: string;
    roster_size: number;
    started: number;
    completed: number;
  }) => capture("assignment_report_viewed", props),
  assignmentDueDateExtended: () => capture("assignment_due_date_extended"),
  assignmentArchived: () => capture("assignment_archived"),

  // Student join + play loop (ANONYMOUS — see the COPPA rule).
  studentJoinStarted: (props: { via: "deep_link" | "manual" }) =>
    capture("student_join_started", props),
  studentClassCodeOk: (props: { classroom_id: string; roster_size: number }) =>
    capture("student_class_code_ok", props),
  studentClassCodeFailed: (props: { reason: string }) =>
    capture("student_class_code_failed", props),
  studentRosterTilePicked: (props: { kind: "login" | "setpin" | "seat" }) =>
    capture("student_roster_tile_picked", props),
  studentNewSelected: () => capture("student_new_selected"),
  studentPinCreated: () => capture("student_pin_created"),
  studentPinIncorrect: () => capture("student_pin_incorrect"),
  studentPinLocked: () => capture("student_pin_locked"),
  studentLoggedIn: (props: { pin_was_set: boolean }) => capture("student_logged_in", props),
  /**
   * Join complete. `parent_sms` is the guardian-invite delivery outcome —
   * a free SMS-deliverability funnel (invalid numbers surface here first).
   */
  /** `attach` = an existing student adding a second class, not a new account. */
  studentJoinedClass: (props: {
    classroom_id: string;
    kind: "seat" | "new" | "attach";
    parent_sms: string;
  }) => capture("student_joined_class", props),
  /** The last step failing — including a schema rejection on the name, which is
   *  the only way to see client-side validation drift from the server's. */
  studentJoinFailed: (props: { reason: string; kind: "seat" | "new" | "attach" }) =>
    capture("student_join_failed", props),
  studentHomeViewed: (props: { active_count: number; finished_count: number; locked: boolean }) =>
    capture("student_home_viewed", props),
  homeworkPlayClicked: (props: { assignment_id: string; started: boolean }) =>
    capture("homework_play_clicked", props),
  homeworkChunkStarted: (props: {
    assignment_id: string;
    chunk_index: number;
    chunk_size: number;
    game_slug: string;
    mastered: number;
    total: number;
    replay: boolean;
  }) => capture("homework_chunk_started", props),
  homeworkChunkCompleted: (props: {
    assignment_id: string;
    chunk_index: number;
    mastered_now: number;
    total: number;
  }) => capture("homework_chunk_completed", props),
  /** Behavioral completion (win screen shown); the canonical twin fires
   *  server-side from the attempts hook as `assignment_completed`. */
  homeworkCompleted: (props: { assignment_id: string; total: number }) =>
    capture("homework_completed", props),
  /** The parent-activation grace window closed and the play path refused. */
  homeworkBlockedUnverified: (props: { assignment_id: string }) =>
    capture("homework_blocked_unverified", props),
  homeworkExpired: (props: { assignment_id: string }) => capture("homework_expired", props),
  /** The "Your work is saved" takeover (home or player). */
  studentLockedScreenViewed: (props: { days_left: number | null; state?: string }) =>
    capture("student_locked_screen_viewed", props),
  /** End of the kid's FIRST set: "we texted your grown-up so your progress saves". */
  studentFirstSetPromptViewed: (props: { assignment_id: string }) =>
    capture("student_first_set_prompt_viewed", props),
  /** The grace banner on the kid's home, once per visit. */
  studentGraceBannerViewed: (props: {
    day_index: number;
    variant: "countdown" | "last_day" | "fix_only";
  }) => capture("student_grace_banner_viewed", props),
  /** The kid's own "Wrong number? Fix it" sheet. No phone numbers in props, ever. */
  studentPhoneFixOpened: (props: { source: "banner" | "expired" | "player_locked" }) =>
    capture("student_phone_fix_opened", props),
  studentPhoneFixCompleted: (props: { source: "banner" | "expired" | "player_locked" }) =>
    capture("student_phone_fix_completed", props),
  studentPhoneFixFailed: (props: {
    source: "banner" | "expired" | "player_locked";
    reason: string;
  }) => capture("student_phone_fix_failed", props),
  homeworkExitPrompted: () => capture("homework_exit_prompted"),
  /** Where kids quit mid-session — the play-loop drop-off signal. */
  homeworkExitConfirmed: (props: { mastered: number; total: number; chunk_index: number }) =>
    capture("homework_exit_confirmed", props),
  homeworkRotateGateShown: () => capture("homework_rotate_gate_shown"),

  // Parent activation + dashboard.
  parentInviteOpened: (props: {
    status: "pending" | "already_verified";
    /** Grace-clock day the parent opened the link on (0..3), when known. */
    day_index?: number | null;
    state?: string;
  }) => capture("parent_invite_opened", props),
  /** token = "Is this you? Text me a code" (prefilled); manual = "Not my number?". */
  parentInviteOtpMode: (props: { mode: "token" | "manual" }) =>
    capture("parent_invite_otp_mode", props),
  parentInviteExpired: () => capture("parent_invite_expired"),
  parentVerifyStarted: () => capture("parent_verify_started"),
  parentOtpSent: () => capture("parent_otp_sent"),
  parentOtpFailed: (props: { reason: string }) => capture("parent_otp_failed", props),
  parentPhoneVerified: () => capture("parent_phone_verified"),
  /** The parent-funnel finish line: activation with explicit COPPA consent.
   *  Opt-in props power the opt-in-rate insights. */
  guardianLinkVerified: (props: { report_card_opt_in: boolean; marketing_opt_in: boolean }) =>
    capture("guardian_link_verified", props),
  parentVerifyFailed: (props: { reason: string }) => capture("parent_verify_failed", props),
  familyDashboardViewed: (props: { kid_count: number }) =>
    capture("family_dashboard_viewed", props),
  familyDashboardEmptyViewed: () => capture("family_dashboard_empty_viewed"),
  familySigninOtpSent: () => capture("family_signin_otp_sent"),
  familySigninCompleted: () => capture("family_signin_completed"),
  familySigninFailed: (props: { reason: string }) => capture("family_signin_failed", props),
  connectKidOpened: (props: { compact: boolean }) => capture("connect_kid_opened", props),
  connectKidClaimed: (props: { kid_count: number }) => capture("connect_kid_claimed", props),
  connectKidNoMatches: () => capture("connect_kid_no_matches"),
  connectKidFailed: () => capture("connect_kid_failed"),
  parentPrefToggled: (props: { pref: "report_card" | "marketing"; value: boolean }) =>
    capture("parent_pref_toggled", props),

  // Shared classroom surfaces.
  /** Which door visitors pick on the /homework role gate. */
  homeworkGateRoleClicked: (props: { role: "student" | "teacher" | "parent" }) =>
    capture("homework_gate_role_clicked", props),
  feedbackWidgetOpened: (props: { page: string }) => capture("feedback_widget_opened", props),
  /** `message_length` only — the text itself goes to the API alone (kid PII
   *  discipline, same as lead_test_worries). */
  feedbackSubmitted: (props: { page: string; message_length: number }) =>
    capture("feedback_submitted", props),
  feedbackSubmitFailed: () => capture("feedback_submit_failed"),
} as const;
